/**
 * F34 (Wave 2D) — admin "reverse package consumption" modal.
 *
 * Composes Modal + warning Banner + FormField + Textarea (reason 1–500).
 * Reversing a consumption credits the session back to the client's package
 * balance (an `exhausted` grant that regains a session flips back to `active`).
 * The reason is mandatory and audit-logged server-side.
 *
 * The reason field validates against the shared
 * `ReverseTrainerConsumptionInputSchema` so the client mirrors the server
 * contract exactly (min 1 / max 500 chars).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea } from '@bravemove/ui-web';
import {
  ReverseTrainerConsumptionInputSchema,
  type ReverseTrainerConsumptionInput,
} from '@bravemove/types';

export interface ReverseConsumptionDialogProps {
  readonly open: boolean;
  /** The consumption id being reversed (shown for confirmation context). */
  readonly consumptionId: string;
  readonly onConfirm: (input: ReverseTrainerConsumptionInput) => Promise<void>;
  readonly onClose: () => void;
}

export function ReverseConsumptionDialog({
  open,
  consumptionId,
  onConfirm,
  onClose,
}: ReverseConsumptionDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(
    () => ReverseTrainerConsumptionInputSchema.safeParse({ reason: reason.trim() }),
    [reason],
  );

  const disableSubmit = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } catch {
      // The caller throws to signal "keep the dialog open" (it has already
      // surfaced the error via a toast). Swallow so the rejection doesn't
      // escape as an unhandled promise rejection; the dialog stays mounted
      // for correction/retry.
    } finally {
      setSubmitting(false);
    }
  };

  const reasonLabel = t('admin:trainer-dashboard.reverse.reason_label', {
    defaultValue: 'Reason (audit-logged)',
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:trainer-dashboard.reverse.title', {
        defaultValue: 'Reverse package consumption',
      })}
      description={t('admin:trainer-dashboard.reverse.description', {
        defaultValue:
          'Credit this session back to the client. Use only when a completed/no-show decrement was recorded in error.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="reverse-consumption-cancel"
          >
            {t('admin:trainer-dashboard.reverse.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="reverse-consumption-confirm"
          >
            {t('admin:trainer-dashboard.reverse.confirm', { defaultValue: 'Reverse consumption' })}
          </Button>
        </>
      }
    >
      <div
        className="flex flex-col gap-4"
        data-testid="reverse-consumption-dialog"
        data-consumption-id={consumptionId}
      >
        <div data-testid="reverse-consumption-warning">
          <Banner tone="warning">
            {t('admin:trainer-dashboard.reverse.warning_banner', {
              defaultValue:
                'This action is audit-logged. The consumption is never deleted — it is marked reversed and the session is credited back.',
            })}
          </Banner>
        </div>

        <FormField label={reasonLabel} required>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="reverse-consumption-reason"
            aria-label={reasonLabel}
            placeholder={t('admin:trainer-dashboard.reverse.reason_placeholder', {
              defaultValue: 'Explain why this decrement is being reversed.',
            })}
          />
        </FormField>
      </div>
    </Modal>
  );
}

export default ReverseConsumptionDialog;
